import React, {useEffect, useMemo, useState} from "react";
import {useLocation} from "react-router-dom";
import {ServiceNavGroupChevron} from "./ServiceNavGroupChevron";
import {
    NavGroup,
    NavGroupChildren,
    NavGroupHeader,
    NavGroupHeaderLabel,
    NavLinkGroupItem,
} from "./ServiceNav.styles";

const getChildPaths = (children: React.ReactNode): string[] => {
    const paths: string[] = [];
    React.Children.forEach(children, (child) => {
        if (!React.isValidElement(child)) {
            return;
        }
        if (child.type === React.Fragment) {
            paths.push(...getChildPaths((child.props as {children?: React.ReactNode}).children));
            return;
        }
        if (typeof (child.props as {to?: string}).to === "string") {
            paths.push((child.props as {to: string}).to);
        }
    });
    return paths;
};

const isPathInGroup = (pathname: string, paths: string[]) =>
    paths.some((path) => pathname === path || pathname.startsWith(`${path}/`));

const enhanceNavChildren = (children: React.ReactNode): React.ReactNode =>
    React.Children.map(children, (child, index) => {
        if (!React.isValidElement(child)) {
            return child;
        }
        if (child.type === React.Fragment) {
            return enhanceNavChildren((child.props as {children?: React.ReactNode}).children);
        }
        if (typeof (child.props as {to?: string}).to === "string") {
            const {to, ...rest} = child.props as {to: string; [key: string]: unknown};
            return <NavLinkGroupItem to={to} {...rest} key={child.key || `child-${index}`} />;
        }
        return child;
    });

const ServiceNavGroup = ({label, children}: {label: string} & React.PropsWithChildren) => {
    const {pathname} = useLocation();
    const childPaths = useMemo(() => getChildPaths(children), [children]);
    const isActive = isPathInGroup(pathname, childPaths);
    const [isOpen, setIsOpen] = useState(isActive);

    useEffect(() => {
        if (isActive) {
            setIsOpen(true);
        }
    }, [isActive]);

    const enhancedChildren = enhanceNavChildren(children);

    return (
        <NavGroup>
            <NavGroupHeader
                type="button"
                isActive={isActive}
                onClick={() => setIsOpen((open: boolean) => !open)}
                aria-expanded={isOpen}
            >
                <NavGroupHeaderLabel>{label}</NavGroupHeaderLabel>
                <ServiceNavGroupChevron isOpen={isOpen} />
            </NavGroupHeader>
            {isOpen && <NavGroupChildren>{enhancedChildren}</NavGroupChildren>}
        </NavGroup>
    );
};

ServiceNavGroup.displayName = "ServiceNavGroup";

export default ServiceNavGroup;
export {ServiceNavGroup};
